import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import LeadDetailModal from './LeadDetailModal';
import FollowUpManager from './FollowUpManager';
import AssignmentNotificationPopup from './AssignmentNotificationPopup';
import NewLeadModalNotification from './NewLeadModalNotification';

const OtherServicesDashboard = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'passport', 'visa', 'tickets', 'followUps'
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [employeeServices, setEmployeeServices] = useState([]); // Employee's service specialization
  const [employeeData, setEmployeeData] = useState(null); // Store full employee data

  // Status change modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({ leadId: null, newStatus: '', reason: '' });
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    // Fetch employee data from database
    fetchEmployeeData();
    fetchLeads();

    // Auto-refresh leads every 3 seconds for real-time updates
    const refreshInterval = setInterval(() => {
      fetchLeads();
    }, 3000);

    // Listen for new assignment notifications
    const handleNewAssignment = (event) => {
      console.log('🎯 New assignment detected, refreshing leads...', event.detail);
      fetchLeads();
    };

    window.addEventListener('newAssignmentNotification', handleNewAssignment);

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('newAssignmentNotification', handleNewAssignment);
    };
  }, []);

  // Fetch employee data from database
  const fetchEmployeeData = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      // Get employee ID from localStorage
      const localEmployeeData = JSON.parse(localStorage.getItem('employeeData') ||
        localStorage.getItem('employeeInfo') || '{}');
      const employeeId = localEmployeeData.id || localEmployeeData._id;

      if (!employeeId) {
        console.log('⚠️ No employee ID found in localStorage');
        return;
      }

      console.log('🔍 Fetching employee data from database for ID:', employeeId);

      // Fetch employee data from API
      const response = await axios.get(`/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const employee = response.data;
      console.log('✅ Employee data fetched from database:', employee);

      // Set employee services from database
      const services = employee.serviceSpecialization || [];
      setEmployeeServices(services);
      setEmployeeData(employee);

      console.log('👤 Employee Service Specialization (from DB):', services);

    } catch (error) {
      console.error('❌ Error fetching employee data:', error);
      // Fallback to localStorage if API fails
      const localEmployeeData = JSON.parse(localStorage.getItem('employeeData') ||
        localStorage.getItem('employeeInfo') || '{}');
      const services = localEmployeeData.serviceSpecialization || [];
      setEmployeeServices(services);
      console.log('⚠️ Using localStorage fallback for services:', services);
    }
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/leads', {
        headers: { Authorization: `Bearer ${token}` }
      });

      let allLeads = [];
      if (Array.isArray(response.data)) {
        allLeads = response.data;
      } else if (response.data.leads && Array.isArray(response.data.leads)) {
        allLeads = response.data.leads;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        allLeads = response.data.data;
      }

      // Get current employee data
      const employeeData = JSON.parse(localStorage.getItem('employeeData') || localStorage.getItem('employeeInfo') || '{}');
      const currentEmployeeId = employeeData.id || employeeData._id;

      console.log('👤 Current Employee ID:', currentEmployeeId);
      console.log('📋 Total leads:', allLeads.length);

      // Filter only other services leads assigned to this employee
      const otherServicesLeads = allLeads.filter(lead => {
        const isOtherServices = lead.leadType === 'other_services';
        const isAssignedToMe = lead.assignedTo === currentEmployeeId ||
          lead.assignedTo?._id === currentEmployeeId ||
          lead.assignedEmployee === currentEmployeeId ||
          lead.assignedEmployee?._id === currentEmployeeId;

        if (isOtherServices && isAssignedToMe) {
          console.log('✅ Lead assigned to me:', lead.customerName);
        }

        return isOtherServices && isAssignedToMe;
      });

      console.log('📊 Filtered leads for this employee:', otherServicesLeads.length);

      setLeads(otherServicesLeads);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  // Handle status change - show modal for on_hold and cancelled
  const handleStatusChange = (leadId, newStatus, currentLead) => {
    if (newStatus === 'on_hold' || newStatus === 'cancelled') {
      // Show modal to get reason
      setStatusChangeData({
        leadId,
        newStatus,
        reason: '',
        customerName: currentLead.customerName,
        serviceType: currentLead.serviceType
      });
      setShowStatusModal(true);
    } else {
      // Direct update for other statuses
      updateLeadStatus(leadId, newStatus, '');
    }
  };

  const updateLeadStatus = async (leadId, newStatus, reason = '') => {
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const updateData = {
        workflowStatus: newStatus
      };

      // Add reason if provided (for on_hold or cancelled)
      if (reason) {
        updateData.statusChangeReason = reason;
        updateData.statusChangedAt = new Date().toISOString();
        updateData.statusChangedBy = employeeData?.name || 'Employee';
      }

      await axios.patch(`/leads/${leadId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchLeads();
      setShowStatusModal(false);
      setStatusChangeData({ leadId: null, newStatus: '', reason: '' });
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Error updating status. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  };

  // Submit status change with reason
  const submitStatusChange = () => {
    if (!statusChangeData.reason.trim()) {
      alert('Please enter a reason for the status change.');
      return;
    }
    updateLeadStatus(statusChangeData.leadId, statusChangeData.newStatus, statusChangeData.reason);
  };

  // Check if employee has access to a service
  const hasServiceAccess = (serviceType) => {
    if (!employeeServices || employeeServices.length === 0) return true; // Show all if no specialization set
    return employeeServices.includes(serviceType);
  };

  const getFilteredLeads = () => {
    if (activeTab === 'all') return leads;
    if (activeTab === 'passport') return leads.filter(l => l.serviceType === 'passport');
    if (activeTab === 'visa') return leads.filter(l => l.serviceType === 'visa');
    if (activeTab === 'tickets') return leads.filter(l => ['railway_ticketing', 'bus_ticketing', 'flight_ticketing', 'cruise_ticketing'].includes(l.serviceType));
    if (activeTab === 'currency') return leads.filter(l => l.serviceType === 'currency');
    if (activeTab === 'insurance') return leads.filter(l => l.serviceType === 'insurance');
    if (activeTab === 'car_rental') return leads.filter(l => l.serviceType === 'car_rental');
    if (activeTab === 'other') return leads.filter(l => l.serviceType === 'other');
    return leads;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className="p-8">Loading...</div>;

  const filteredLeads = getFilteredLeads();

  // Get current employee ID for notifications (use employeeData state if available)
  const currentEmployeeId = employeeData?.id || employeeData?._id ||
    JSON.parse(localStorage.getItem('employeeData') || localStorage.getItem('employeeInfo') || '{}').id;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modal Notification - Center of Screen */}
      <NewLeadModalNotification employeeId={currentEmployeeId} />

      {/* Corner Notification Popup */}
      <AssignmentNotificationPopup employeeId={currentEmployeeId} />

      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-teal-900 to-teal-800 text-white shadow-2xl flex flex-col fixed h-screen z-40 left-0 top-0">
        <div className="p-6 border-b border-teal-700">
          <h2 className="text-xl font-bold">Other Services</h2>
          <p className="text-sm text-teal-200 mt-1">Dashboard</p>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'all'
              ? 'bg-white text-teal-900 shadow-lg font-semibold'
              : 'text-teal-100 hover:bg-teal-700 hover:text-white'
              }`}
          >
            <span className="text-base">📋</span>
            <div className="flex-1">
              <div className="font-medium">All Services</div>
              <div className="text-xs opacity-75">{leads.length} leads</div>
            </div>
          </button>

          {/* Passport - Only show if employee has access */}
          {hasServiceAccess('passport') && (
            <button
              onClick={() => setActiveTab('passport')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'passport'
                ? 'bg-white text-teal-900 shadow-lg font-semibold'
                : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                }`}
            >
              <span className="text-base">🛂</span>
              <div className="flex-1">
                <div className="font-medium">Passport</div>
                <div className="text-xs opacity-75">{leads.filter(l => l.serviceType === 'passport').length} leads</div>
              </div>
            </button>
          )}

          {/* Visa - Only show if employee has access */}
          {hasServiceAccess('visa') && (
            <button
              onClick={() => setActiveTab('visa')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'visa'
                ? 'bg-white text-teal-900 shadow-lg font-semibold'
                : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                }`}
            >
              <span className="text-base">✈️</span>
              <div className="flex-1">
                <div className="font-medium">Visa</div>
                <div className="text-xs opacity-75">{leads.filter(l => l.serviceType === 'visa').length} leads</div>
              </div>
            </button>
          )}

          {/* Tickets - Show if employee has any ticketing service */}
          {(hasServiceAccess('railway_ticketing') || hasServiceAccess('bus_ticketing') ||
            hasServiceAccess('flight_ticketing') || hasServiceAccess('cruise_ticketing')) && (
              <button
                onClick={() => setActiveTab('tickets')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'tickets'
                  ? 'bg-white text-teal-900 shadow-lg font-semibold'
                  : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                  }`}
              >
                <span className="text-base">🎫</span>
                <div className="flex-1">
                  <div className="font-medium">Tickets</div>
                  <div className="text-xs opacity-75">
                    {leads.filter(l => ['railway_ticketing', 'bus_ticketing', 'flight_ticketing', 'cruise_ticketing'].includes(l.serviceType)).length} leads
                  </div>
                </div>
              </button>
            )}

          {/* Currency - Only show if employee has access */}
          {hasServiceAccess('currency') && (
            <button
              onClick={() => setActiveTab('currency')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'currency'
                ? 'bg-white text-teal-900 shadow-lg font-semibold'
                : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                }`}
            >
              <span className="text-base">💱</span>
              <div className="flex-1">
                <div className="font-medium">Currency</div>
                <div className="text-xs opacity-75">{leads.filter(l => l.serviceType === 'currency').length} leads</div>
              </div>
            </button>
          )}

          {/* Insurance - Only show if employee has access */}
          {hasServiceAccess('insurance') && (
            <button
              onClick={() => setActiveTab('insurance')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'insurance'
                ? 'bg-white text-teal-900 shadow-lg font-semibold'
                : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                }`}
            >
              <span className="text-base">🛡️</span>
              <div className="flex-1">
                <div className="font-medium">Insurance</div>
                <div className="text-xs opacity-75">{leads.filter(l => l.serviceType === 'insurance').length} leads</div>
              </div>
            </button>
          )}

          {/* Car Rental - Only show if employee has access */}
          {hasServiceAccess('car_rental') && (
            <button
              onClick={() => setActiveTab('car_rental')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'car_rental'
                ? 'bg-white text-teal-900 shadow-lg font-semibold'
                : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                }`}
            >
              <span className="text-base">🚗</span>
              <div className="flex-1">
                <div className="font-medium">Car Rental</div>
                <div className="text-xs opacity-75">{leads.filter(l => l.serviceType === 'car_rental').length} leads</div>
              </div>
            </button>
          )}

          {/* Other Services - Only show if employee has access */}
          {hasServiceAccess('other') && (
            <button
              onClick={() => setActiveTab('other')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'other'
                ? 'bg-white text-teal-900 shadow-lg font-semibold'
                : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                }`}
            >
              <span className="text-base">📦</span>
              <div className="flex-1">
                <div className="font-medium">Other</div>
                <div className="text-xs opacity-75">{leads.filter(l => l.serviceType === 'other').length} leads</div>
              </div>
            </button>
          )}

          <button
            onClick={() => setActiveTab('followUps')}
            className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${activeTab === 'followUps'
              ? 'bg-white text-orange-600 shadow-lg font-semibold'
              : 'text-teal-100 hover:bg-teal-700 hover:text-white'
              }`}
          >
            <span className="text-base">📅</span>
            <div className="flex-1">
              <div className="font-medium">Follow-ups</div>
              <div className="text-xs opacity-75">Manage tasks</div>
            </div>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="ml-64 min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats Cards - Only show cards for services employee has access to */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {/* All Services - Always show */}
            <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-teal-500">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-600 text-xs font-medium">All Services</span>
                <span className="text-xs">📋</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{leads.length}</p>
            </div>

            {/* Passport - Only if employee has access */}
            {hasServiceAccess('passport') && (
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 text-xs font-medium">Passport</span>
                  <span className="text-xs">🛂</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{leads.filter(l => l.serviceType === 'passport').length}</p>
              </div>
            )}

            {/* Visa - Only if employee has access */}
            {hasServiceAccess('visa') && (
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-purple-500">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 text-xs font-medium">Visa</span>
                  <span className="text-xs">✈️</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{leads.filter(l => l.serviceType === 'visa').length}</p>
              </div>
            )}

            {/* Tickets - Only if employee has access to any ticketing service */}
            {(hasServiceAccess('railway_ticketing') || hasServiceAccess('bus_ticketing') ||
              hasServiceAccess('flight_ticketing') || hasServiceAccess('cruise_ticketing')) && (
                <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-orange-500">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-600 text-xs font-medium">Tickets</span>
                    <span className="text-xs">🎫</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">
                    {leads.filter(l => ['railway_ticketing', 'bus_ticketing', 'flight_ticketing', 'cruise_ticketing'].includes(l.serviceType)).length}
                  </p>
                </div>
              )}

            {/* Currency - Only if employee has access */}
            {hasServiceAccess('currency') && (
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 text-xs font-medium">Currency</span>
                  <span className="text-xs">💱</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{leads.filter(l => l.serviceType === 'currency').length}</p>
              </div>
            )}

            {/* Insurance - Only if employee has access */}
            {hasServiceAccess('insurance') && (
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-red-500">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 text-xs font-medium">Insurance</span>
                  <span className="text-xs">🛡️</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{leads.filter(l => l.serviceType === 'insurance').length}</p>
              </div>
            )}

            {/* Car Rental - Only if employee has access */}
            {hasServiceAccess('car_rental') && (
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-yellow-500">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 text-xs font-medium">Car Rental</span>
                  <span className="text-xs">🚗</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{leads.filter(l => l.serviceType === 'car_rental').length}</p>
              </div>
            )}

            {/* Other Services - Only if employee has access */}
            {hasServiceAccess('other') && (
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-gray-500">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 text-xs font-medium">Other</span>
                  <span className="text-xs">📦</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{leads.filter(l => l.serviceType === 'other').length}</p>
              </div>
            )}
          </div>

          {/* Leads Table */}
          {activeTab !== 'followUps' && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700 border-b">
                <h2 className="text-xl font-bold text-white">
                  {activeTab === 'all' && '📋 All Services Leads'}
                  {activeTab === 'passport' && '🛂 Passport Services'}
                  {activeTab === 'visa' && '✈️ Visa Services'}
                  {activeTab === 'tickets' && '🎫 Ticketing Services'}
                  {activeTab === 'currency' && '💱 Currency Exchange'}
                  {activeTab === 'insurance' && '🛡️ Insurance Services'}
                  {activeTab === 'car_rental' && '🚗 Car Rental Services'}
                  {activeTab === 'other' && '📦 Other Services'}
                </h2>
                <p className="text-teal-100 text-sm mt-1">{filteredLeads.length} leads</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">SR No</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Service Type</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">👤 Added By</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLeads.map(lead => (
                      <tr
                        key={lead._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full shadow-md">
                            <span className="text-sm font-bold text-white">
                              {lead.serialNumber || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="font-semibold text-teal-600 hover:text-teal-800 hover:underline text-left transition-colors"
                            >
                              {lead.customerName}
                            </button>
                            <div className="text-sm text-gray-500 mt-1">{lead.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-teal-100 text-teal-800">
                            {lead.serviceType?.replace(/_/g, ' ').toUpperCase() || 'Other'}
                          </span>
                        </td>
                        {/* Added By Cell */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {lead.createdByName ? (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-white text-xs font-bold">
                                  {lead.createdByName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-800">{lead.createdByName}</span>
                                {lead.createdByDepartment && (
                                  <span className="text-xs text-gray-500">{lead.createdByDepartment}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Unknown</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <select
                              value={lead.workflowStatus || 'new'}
                              onChange={(e) => handleStatusChange(lead._id, e.target.value, lead)}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-full border-0 focus:ring-2 focus:ring-teal-500 ${lead.workflowStatus === 'completed' ? 'bg-green-100 text-green-800' :
                                lead.workflowStatus === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                  lead.workflowStatus === 'on_hold' ? 'bg-yellow-100 text-yellow-800' :
                                    lead.workflowStatus === 'cancelled' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                }`}
                            >
                              <option value="new">New</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="on_hold">On Hold</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            {/* Show reason if status is on_hold or cancelled */}
                            {(lead.workflowStatus === 'on_hold' || lead.workflowStatus === 'cancelled') && lead.statusChangeReason && (
                              <div className="text-xs text-gray-500 italic max-w-[150px] truncate" title={lead.statusChangeReason}>
                                📝 {lead.statusChangeReason}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => {
                                setSelectedLeadForFollowUp(lead);
                                setShowFollowUpForm(true);
                              }}
                              className="px-2 py-1 bg-gradient-to-r from-orange-600 to-orange-700 text-white text-xs font-medium rounded hover:from-orange-700 hover:to-orange-800 transition-all"
                            >
                              Follow-up
                            </button>
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="px-2 py-1 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-xs font-medium rounded hover:from-teal-700 hover:to-teal-800 transition-all"
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredLeads.length === 0 && (
                <div className="text-center py-16 px-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                    <span className="text-2xl">📋</span>
                  </div>
                  <p className="text-gray-700 text-xl font-semibold mb-2">No leads found</p>
                  <p className="text-gray-500 text-sm">Leads will appear here once assigned</p>
                </div>
              )}
            </div>
          )}

          {/* Follow-up Manager */}
          {activeTab === 'followUps' && (
            <FollowUpManager department="other_services" leads={leads} />
          )}
        </div>
      </div>

      {/* Follow-up Modal */}
      {showFollowUpForm && selectedLeadForFollowUp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-yellow-600 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">📅 Create Follow-up</h2>
                <p className="text-sm opacity-90">For: {selectedLeadForFollowUp.customerName}</p>
              </div>
              <button
                onClick={() => {
                  setShowFollowUpForm(false);
                  setSelectedLeadForFollowUp(null);
                }}
                className="text-white hover:text-gray-200 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <FollowUpManager
                department="other_services"
                leadId={selectedLeadForFollowUp._id}
                customerName={selectedLeadForFollowUp.customerName}
                customerPhone={selectedLeadForFollowUp.contactNumber}
                customerEmail={selectedLeadForFollowUp.email}
                showOnlyForm={true}
                onSuccess={() => {
                  setShowFollowUpForm(false);
                  setSelectedLeadForFollowUp(null);
                  fetchLeads();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
        />
      )}

      {/* Status Change Reason Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className={`px-6 py-4 ${statusChangeData.newStatus === 'cancelled'
              ? 'bg-gradient-to-r from-red-600 to-red-700'
              : 'bg-gradient-to-r from-yellow-500 to-orange-500'
              }`}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {statusChangeData.newStatus === 'cancelled' ? '❌ Cancel Lead' : '⏸️ Put On Hold'}
                  </h2>
                  <p className="text-sm text-white opacity-90 mt-1">
                    {statusChangeData.customerName} - {statusChangeData.serviceType?.replace(/_/g, ' ').toUpperCase() || 'Other'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusChangeData({ leadId: null, newStatus: '', reason: '' });
                  }}
                  className="text-white hover:text-gray-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {statusChangeData.newStatus === 'cancelled'
                  ? '🔴 Cancellation Reason *'
                  : '🟡 On Hold Reason *'}
              </label>
              <textarea
                value={statusChangeData.reason}
                onChange={(e) => setStatusChangeData({ ...statusChangeData, reason: e.target.value })}
                placeholder={statusChangeData.newStatus === 'cancelled'
                  ? 'Enter reason for cancellation (e.g., Customer not interested, Price too high, Chose competitor, etc.)'
                  : 'Enter reason for putting on hold (e.g., Customer needs time, Waiting for documents, Budget not confirmed, etc.)'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                rows={4}
                autoFocus
              />

              {/* Quick Reason Buttons */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2">Quick select:</p>
                <div className="flex flex-wrap gap-2">
                  {statusChangeData.newStatus === 'cancelled' ? (
                    <>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Customer not interested' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Customer not interested</button>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Price too high' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Price too high</button>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Chose competitor' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Chose competitor</button>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Trip cancelled by customer' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Trip cancelled</button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Customer needs more time to decide' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Needs more time</button>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Waiting for documents' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Waiting for documents</button>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Budget not confirmed' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Budget not confirmed</button>
                      <button
                        onClick={() => setStatusChangeData({ ...statusChangeData, reason: 'Travel dates not finalized' })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >Dates not finalized</button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusChangeData({ leadId: null, newStatus: '', reason: '' });
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                disabled={statusLoading}
              >
                Cancel
              </button>
              <button
                onClick={submitStatusChange}
                disabled={statusLoading || !statusChangeData.reason.trim()}
                className={`px-4 py-2 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed ${statusChangeData.newStatus === 'cancelled'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-orange-500 hover:bg-orange-600'
                  }`}
              >
                {statusLoading ? 'Saving...' : statusChangeData.newStatus === 'cancelled' ? 'Confirm Cancel' : 'Confirm On Hold'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherServicesDashboard;
